/**
 * Asset download analytics (RFC-1577, doc P0 `asset_download_action`).
 *
 * One unified contract for every image / video / audio download the console
 * hands to the browser: node toolbar, variant cards, the palette's Download
 * modal (canvas bottom bar or list view) and the timeline's source-audio
 * download. It supersedes `canvas_video_download_initiated`, which is no longer
 * emitted — the doc requires one initiation per attempt, never both events.
 *
 *   initiated — recorded once per asset attempt, as the attempt starts handing
 *               off to the browser download (iframe / anchor navigation).
 *   failed    — only when THAT attempt fails observably in the client. Browser
 *               download completion is not observable, so there is no
 *               saved/success row.
 *
 * Every attempt gets its own `download_id`; the attempts of one bulk click
 * share a `bulk_action_id`. Properties are ids, enums and buckets only — never
 * a file name, asset path or URL (`format` is the bare extension).
 *
 * Telemetry must never throw or change behaviour: every emitter swallows its
 * own failures.
 */

import type { NodePresentation } from '../components/canvas-v2/NodePresentationContext'
import { readWorkspaceViewMode } from '../components/workspace/useWorkspaceViewPreference'
import { createNadDebug } from '../utils/debugLog'
import { countBucket } from './buckets'
import { track } from './events'
import type {
  AssetDownloadActionProps,
  AssetDownloadEntryPoint,
  AssetDownloadErrorCategory,
  AssetDownloadTarget,
  OutputMediaType,
  WorkspaceViewMode,
} from './generated/analyticsEvents'
import { getProjectVisitId } from './projectVisit'

const log = createNadDebug('dw:analytics-events:download')

export interface AssetDownloadBulk {
  /** Shared by every attempt of one bulk click — see `newBulkActionId()`. */
  id: string
  /** Assets in the bulk action. */
  count: number
}

export interface AssetDownloadContext {
  projectId: string
  entryPoint: AssetDownloadEntryPoint
  target: AssetDownloadTarget
  viewMode: WorkspaceViewMode
  mediaType?: OutputMediaType
  nodeId?: string
  /** `${nodeId}:${index}` — see `outputVariantId()` in outputVariantId.ts. */
  variantId?: string
  /** File name (or path) to derive the bare extension from; never sent itself. */
  filename?: string
  /** Present for bulk actions; absent ⇒ `download_kind: single`. */
  bulk?: AssetDownloadBulk
}

export interface AssetDownloadAttempt {
  readonly downloadId: string
  /** Record an observable failure of this attempt. At most once; never throws. */
  failed(category: AssetDownloadErrorCategory): void
}

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/** One id per bulk click, shared by each asset attempt it starts. */
export function newBulkActionId(): string {
  return randomId()
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif', 'bmp', 'heic', 'heif', 'svg'])
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm', 'm4v', 'mkv', 'avi'])
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'm4a', 'aac', 'ogg', 'oga', 'flac', 'opus'])

/** Bare lowercase extension of a file name / path (query and hash ignored), or undefined. */
export function downloadFormat(filename: string | undefined): string | undefined {
  if (!filename) return undefined
  const base = filename.split(/[?#]/)[0].split('/').pop() ?? ''
  const dot = base.lastIndexOf('.')
  if (dot < 0) return undefined
  const ext = base.slice(dot + 1).toLowerCase()
  // Extensions only — anything else could be free text.
  return /^[a-z0-9]{1,5}$/.test(ext) ? ext : undefined
}

/** image | video | audio from a file extension; undefined when unknown. */
export function mediaTypeFromFilename(filename: string | undefined): OutputMediaType | undefined {
  const ext = downloadFormat(filename)
  if (!ext) return undefined
  if (IMAGE_EXTENSIONS.has(ext)) return 'image'
  if (VIDEO_EXTENSIONS.has(ext)) return 'video'
  if (AUDIO_EXTENSIONS.has(ext)) return 'audio'
  return undefined
}

/**
 * Workspace view for a node's presentation: `list` / `detail` are the
 * simplified view's cards, and `background` is the hidden canvas that only
 * exists while the simplified view is active.
 */
export function viewModeForPresentation(presentation: NodePresentation): WorkspaceViewMode {
  return presentation === 'canvas' ? 'canvas' : 'simplified'
}

/** The current workspace view outside a node (modals, timeline). Never throws. */
export function currentWorkspaceViewMode(): WorkspaceViewMode {
  try {
    return readWorkspaceViewMode()
  } catch {
    return 'canvas'
  }
}

/**
 * Record `initiated` for one asset attempt and return a handle for its
 * failure. Call as the attempt starts its handoff — once per asset, never per
 * retry of the same click's internals. Never throws; a telemetry failure still
 * returns a usable (no-op) handle.
 */
export function beginAssetDownload(ctx: AssetDownloadContext): AssetDownloadAttempt {
  const downloadId = randomId()
  let base: Omit<AssetDownloadActionProps, 'action' | 'error_category'> | null = null
  try {
    base = {
      project_id: ctx.projectId,
      project_visit_id: getProjectVisitId(ctx.projectId),
      download_id: downloadId,
      entry_point: ctx.entryPoint,
      download_kind: ctx.bulk ? 'bulk' : 'single',
      download_target: ctx.target,
      view_mode: ctx.viewMode,
      media_type: ctx.mediaType ?? mediaTypeFromFilename(ctx.filename),
      node_id: ctx.nodeId,
      variant_id: ctx.variantId,
      format: downloadFormat(ctx.filename),
      bulk_action_id: ctx.bulk?.id,
      bulk_count_bucket: ctx.bulk ? countBucket(ctx.bulk.count) : undefined,
    }
    emit({ ...base, action: 'initiated' })
  } catch (err) {
    log({ event: 'download-analytics-error', error_name: err instanceof Error ? err.name : typeof err })
  }
  let failedOnce = false
  return {
    downloadId,
    failed(category) {
      if (failedOnce || !base) return
      failedOnce = true
      try {
        emit({ ...base, action: 'failed', error_category: category })
      } catch (err) {
        log({ event: 'download-analytics-error', error_name: err instanceof Error ? err.name : typeof err })
      }
    },
  }
}

function emit(props: AssetDownloadActionProps): void {
  log({
    event: 'asset-download-action',
    action: props.action,
    download_id: props.download_id,
    entry_point: props.entry_point,
    download_kind: props.download_kind,
    download_target: props.download_target,
    media_type: props.media_type,
    node_id: props.node_id,
    variant_id: props.variant_id,
    format: props.format,
    bulk_action_id: props.bulk_action_id,
    error_category: props.error_category,
  })
  track('asset_download_action', props)
}
